/**
 * Primary weapons (spec §6): rate of fire, energy cost, alternating muzzles, gimbal assist toward the
 * target's lead point and PDC spread. Fires pooled raycast projectiles (projectiles.ts). Secondary
 * (torpedo) fire is torpedoes.ts (G3). Allocation-free per tick (D-014): scratch objects are module-level.
 */
import { rotateVec, type Quat, type Vec3 } from '../core/math'
import { nextFloat, type Rng } from '../core/rng'
import type { ProjectileWeaponDef, ResolvedMount, ShipDef, WeaponsData } from '../data/schemas'
import { Control, Energy, Hull, Life, Pos64, Rot, Ship, Target, Team, Vel, Weapons } from '../world/components'
import { emitEvent, EV, type EventRing } from './events'
import { isHostile } from './hostility'
import { assistCorrection, rotateToward } from './aimAssist'
import { createLeadSolution, solveLead } from './lead'
import { spawnProjectile, type ProjectilePool } from './projectiles'

/** Cooldown slack (s) so float32 rounding in `Weapons.cooldownPrimary` never delays a shot by a tick (< 0.1% of a 60 Hz tick). */
const COOLDOWN_EPS = 1e-5
/** Safety cap on shots per ship per tick (a rate above the tick rate fires several per tick). */
const MAX_SHOTS_PER_TICK = 8

// Scratch (D-014).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const muzzlePos: Vec3 = { x: 0, y: 0, z: 0 }
const dir: Vec3 = { x: 0, y: 0, z: 0 }
const leadDir: Vec3 = { x: 0, y: 0, z: 0 }
const lead = createLeadSolution()

function isPrimaryIn(m: ResolvedMount, slot: number): boolean {
  return m.group === 'primary' && m.slot === slot
}

/** Number of primary mounts in `slot`. */
export function primaryMountCount(def: ShipDef, slot: number): number {
  let n = 0
  for (let i = 0; i < def.mounts.length; i++) if (isPrimaryIn(def.mounts[i], slot)) n++
  return n
}

/** The `ordinal`-th primary mount of `slot` (in loadout order), or undefined. */
function primaryMount(def: ShipDef, slot: number, ordinal: number): ResolvedMount | undefined {
  let n = 0
  for (let i = 0; i < def.mounts.length; i++) {
    const m = def.mounts[i]
    if (!isPrimaryIn(m, slot)) continue
    if (n === ordinal) return m
    n++
  }
  return undefined
}

/** Largest muzzle count among the primary mounts of `slot`. */
function maxMuzzles(def: ShipDef, slot: number): number {
  let n = 0
  for (let i = 0; i < def.mounts.length; i++) {
    const m = def.mounts[i]
    if (isPrimaryIn(m, slot) && m.muzzles.length > n) n = m.muzzles.length
  }
  return n
}

/**
 * Selects primary slot `slot` (key 1 → 0, key 2 → 1) for ship `eid` when `def` has a primary mount
 * there. Changing slot restarts the muzzle alternation. Returns true when that slot is now selected.
 */
export function selectPrimary(eid: number, slot: number, def: ShipDef): boolean {
  if (primaryMountCount(def, slot) === 0) return false
  if (Weapons.primaryIndex[eid] !== slot) {
    Weapons.primaryIndex[eid] = slot
    Weapons.alternate[eid] = 0
  }
  return true
}

/** Selects the next primary slot that has a mount (pad X). Returns the selected slot. */
export function cyclePrimary(eid: number, def: ShipDef): number {
  const current = Weapons.primaryIndex[eid]
  for (let k = 1; k <= 2; k++) {
    const slot = (current + k) % 2
    if (selectPrimary(eid, slot, def)) return slot
  }
  return current
}

/**
 * Solves the lead of target `t` for a shot from `muzzlePos` by ship `eid` and returns the angle (radians)
 * between `dir` and the lead point, writing the unit lead direction to `leadDir`. -1 when unsolvable.
 */
function leadAngle(eid: number, t: number, speed: number): number {
  solveLead(muzzlePos.x, muzzlePos.y, muzzlePos.z, Vel.x[eid], Vel.y[eid], Vel.z[eid], Pos64.x[t], Pos64.y[t], Pos64.z[t], Vel.x[t], Vel.y[t], Vel.z[t], speed, lead)
  const ax = lead.x - muzzlePos.x
  const ay = lead.y - muzzlePos.y
  const az = lead.z - muzzlePos.z
  const dist = Math.sqrt(ax * ax + ay * ay + az * az)
  if (dist <= 0) return -1
  leadDir.x = ax / dist
  leadDir.y = ay / dist
  leadDir.z = az / dist
  const cos = leadDir.x * dir.x + leadDir.y * dir.y + leadDir.z * dir.z
  return Math.acos(cos > 1 ? 1 : cos < -1 ? -1 : cos)
}

function isLiveOther(eid: number, t: number): boolean {
  return t >= 0 && t !== eid && Life.alive[t] === 1 && Hull.value[t] > 0
}

/**
 * The ship whose lead point the guns of `eid` (a shot from `from` along the unit `along`) converge on: its selected target when it has one, otherwise the
 * hostile (other team) whose lead point is nearest the muzzle direction inside the widest cone the gimbal
 * or the assist reaches. Leaves the chosen target's lead direction in `leadDir` and returns its angle
 * (radians), or -1 when there is none. Allocation-free.
 */
export function pickAimTarget(eid: number, ships: ArrayLike<number>, speed: number, weaponsData: WeaponsData, from: Readonly<Vec3>, along: Readonly<Vec3>): number {
  muzzlePos.x = from.x
  muzzlePos.y = from.y
  muzzlePos.z = from.z
  dir.x = along.x
  dir.y = along.y
  dir.z = along.z
  const t = Target.eid[eid]
  if (isLiveOther(eid, t)) return leadAngle(eid, t, speed)
  const a = weaponsData.aimAssist
  const reach = a.cone > weaponsData.gimbal ? a.cone : weaponsData.gimbal
  let best = -1
  let bestAngle = reach
  for (let i = 0; i < ships.length; i++) {
    const o = ships[i]
    if (!isLiveOther(eid, o) || !isHostile(Team.id[eid], Team.id[o])) continue
    const ang = leadAngle(eid, o, speed)
    if (ang >= 0 && ang < bestAngle) {
      best = o
      bestAngle = ang
    }
  }
  if (best < 0) return -1
  return leadAngle(eid, best, speed)
}

/**
 * Turns the shot direction in `dir` toward the aim target's lead point (spec §6 gimbal plus aim assist):
 * a full snap inside the base gimbal, else up to the assist gimbal weighted by how centred the lead is.
 */
function applyGimbal(eid: number, ships: ArrayLike<number>, speed: number, weaponsData: WeaponsData): void {
  const angle = pickAimTarget(eid, ships, speed, weaponsData, muzzlePos, dir)
  if (angle <= 0) return
  const turn = assistCorrection(angle, weaponsData.gimbal, weaponsData.aimAssist, Weapons.assist[eid])
  rotateToward(dir, leadDir.x, leadDir.y, leadDir.z, angle, turn)
}

/** Rotates `dir` by a random angle uniformly distributed over the cone of half-angle `spread` (radians). */
function applySpread(spread: number, rng: Rng): void {
  if (spread <= 0) return
  const cosT = 1 - nextFloat(rng) * (1 - Math.cos(spread))
  const sinT = Math.sqrt(1 - cosT * cosT)
  const phi = nextFloat(rng) * Math.PI * 2
  // An orthonormal basis (b1, b2) perpendicular to dir, from whichever world axis is least parallel.
  const useX = Math.abs(dir.x) < 0.9
  const hx = useX ? 1 : 0
  const hy = useX ? 0 : 1
  let b1x = -dir.z * hy
  let b1y = dir.z * hx
  let b1z = dir.x * hy - dir.y * hx
  const l = Math.sqrt(b1x * b1x + b1y * b1y + b1z * b1z)
  b1x /= l
  b1y /= l
  b1z /= l
  const b2x = dir.y * b1z - dir.z * b1y
  const b2y = dir.z * b1x - dir.x * b1z
  const b2z = dir.x * b1y - dir.y * b1x
  const c = Math.cos(phi) * sinT
  const s = Math.sin(phi) * sinT
  dir.x = dir.x * cosT + b1x * c + b2x * s
  dir.y = dir.y * cosT + b1y * c + b2y * s
  dir.z = dir.z * cosT + b1z * c + b2z * s
}

/** Fires one shot of ship `eid`'s selected group from its next muzzle. Returns false when the pool dropped it. */
function fireShot(eid: number, ships: ArrayLike<number>, def: ShipDef, slot: number, weaponsData: WeaponsData, pool: ProjectilePool, events: EventRing, rng: Rng): boolean {
  const mounts = primaryMountCount(def, slot)
  const barrels = maxMuzzles(def, slot)
  const cycle = mounts * barrels
  const k = Weapons.alternate[eid] % cycle
  Weapons.alternate[eid] = (k + 1) % cycle
  // Alternate sides first, then barrels: L0, R0, L1, R1 (Kestrel PDCs); L, R (interceptor lasers).
  const mount = primaryMount(def, slot, k % mounts)
  if (!mount) return false
  const muzzle = mount.muzzles[Math.floor(k / mounts) % mount.muzzles.length]
  const w = weaponsData.weapons[mount.weapon]
  if (w.kind !== 'projectile') return false

  q.x = Rot.x[eid]
  q.y = Rot.y[eid]
  q.z = Rot.z[eid]
  q.w = Rot.w[eid]
  rotateVec(q, muzzle.pos.x, muzzle.pos.y, muzzle.pos.z, muzzlePos)
  muzzlePos.x += Pos64.x[eid]
  muzzlePos.y += Pos64.y[eid]
  muzzlePos.z += Pos64.z[eid]
  rotateVec(q, muzzle.dir.x, muzzle.dir.y, muzzle.dir.z, dir)
  applyGimbal(eid, ships, w.speed, weaponsData)
  return launchRound(eid, mount.weapon, w, muzzlePos, dir, pool, events, rng)
}

/**
 * Launches one round of projectile weapon `weapon` (`w`) for ship `eid` from world point `from` along the unit
 * direction `along`: applies the weapon's spread, pays its energy cost, spawns the pooled round with the ship's
 * velocity added and emits `fire`. The one gun path shared by primaries and turrets (M6 P20, D-247's fairness
 * rule). Returns false when the pool dropped the round. Allocation-free.
 */
export function launchRound(
  eid: number,
  weapon: number,
  w: ProjectileWeaponDef,
  from: Readonly<Vec3>,
  along: Readonly<Vec3>,
  pool: ProjectilePool,
  events: EventRing,
  rng: Rng,
): boolean {
  muzzlePos.x = from.x
  muzzlePos.y = from.y
  muzzlePos.z = from.z
  dir.x = along.x
  dir.y = along.y
  dir.z = along.z
  applySpread(w.spread, rng)

  Energy.value[eid] -= w.energyCost
  const i = spawnProjectile(
    pool,
    muzzlePos.x,
    muzzlePos.y,
    muzzlePos.z,
    Vel.x[eid] + dir.x * w.speed,
    Vel.y[eid] + dir.y * w.speed,
    Vel.z[eid] + dir.z * w.speed,
    w.range / w.speed,
    eid,
    Team.id[eid],
    weapon,
  )
  emitEvent(events, EV.fire, muzzlePos.x, muzzlePos.y, muzzlePos.z, dir.x, dir.y, dir.z, eid, -1, weapon, 0)
  return i >= 0
}

/**
 * Primary fire for every ship in `ships` (spec §4.2 step 3, before `stepProjectiles`). A ship holding
 * `Control.firePrimary` fires its selected primary group (`Weapons.primaryIndex` → the mounts with
 * `group: 'primary'` and that `slot`) at the group weapon's rate, paying `energyCost` per shot from
 * `Energy` (blocked while energy is short), alternating muzzles; each shot is aimed by the gimbal and
 * spread, inherits the ship's velocity and emits `fire`. `rng` is the seeded sim RNG (spread only).
 */
export function stepPrimaryWeapons(
  ships: ArrayLike<number>,
  defs: readonly ShipDef[],
  weaponsData: WeaponsData,
  pool: ProjectilePool,
  events: EventRing,
  // Sim time: part of the shared combat-step signature; primary fire needs none.
  _now: number,
  dt: number,
  rng: Rng,
): void {
  for (let s = 0; s < ships.length; s++) {
    const eid = ships[s]
    let cd = Weapons.cooldownPrimary[eid]
    const firing = Control.firePrimary[eid] === 1 && Life.alive[eid] === 1
    let blocked = true
    if (firing) {
      const def = defs[Ship.defIndex[eid]]
      const slot = Weapons.primaryIndex[eid]
      const first = primaryMount(def, slot, 0)
      const w = first ? weaponsData.weapons[first.weapon] : undefined
      if (w && w.kind === 'projectile' && maxMuzzles(def, slot) > 0) {
        const interval = 1 / w.rate
        let shots = 0
        blocked = false
        while (cd <= COOLDOWN_EPS && shots < MAX_SHOTS_PER_TICK) {
          if (Energy.value[eid] < w.energyCost) {
            blocked = true
            break
          }
          fireShot(eid, ships, def, slot, weaponsData, pool, events, rng)
          cd += interval
          shots++
        }
      }
    }
    cd -= dt
    // Idle or blocked: the gun is ready at once but banks no extra shots. While firing, the carry
    // (under one tick) keeps rates that do not divide the tick rate exact on average.
    if (cd < 0 && blocked) cd = 0
    Weapons.cooldownPrimary[eid] = cd
  }
}
